import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

const absoluteUrl = (value, siteUrl) => {
  if (!value) return ""
  if (/^https?:\/\//i.test(value)) return value
  if (value.startsWith("//")) return `https:${value}`
  return `${siteUrl}${value.startsWith("/") ? value : `/${value}`}`
}

const canonicalUrl = (path, siteUrl) => {
  if (!path) return siteUrl
  const normalized = path.startsWith("/") ? path : `/${path}`
  return `${siteUrl}${normalized === "/" ? "" : normalized.replace(/\/+$/, "")}`
}

function Seo({ title, description, path, image, type, noindex, children }) {
  const data = useStaticQuery(graphql`
    query SeoMetadata {
      site {
        siteMetadata {
          title
          description
          keywords
          author
          siteUrl
          indexable
        }
      }
    }
  `)

  const meta = data?.site?.siteMetadata || {}
  const siteTitle = meta.title || ""
  const pageTitle =
    title && title !== siteTitle ? `${title} | ${siteTitle}` : siteTitle
  const metaDescription = description || meta.description || ""
  const url = canonicalUrl(path, meta.siteUrl || "")
  const ogImage = absoluteUrl(image, meta.siteUrl || "")
  const blocked = noindex || meta.indexable === false

  return (
    <Helmet htmlAttributes={{ lang: "es" }} title={pageTitle}>
      <meta name="description" content={metaDescription} />
      {meta.keywords ? <meta name="keywords" content={meta.keywords} /> : null}
      <meta
        name="robots"
        content={blocked ? "noindex, nofollow" : "index, follow"}
      />
      <link rel="canonical" href={url} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content="es_MX" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={url} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      <meta
        name="twitter:card"
        content={ogImage ? "summary_large_image" : "summary"}
      />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
      {meta.author ? <meta name="author" content={meta.author} /> : null}
      {children}
    </Helmet>
  )
}

Seo.defaultProps = {
  title: "",
  description: "",
  path: "/",
  image: "",
  type: "website",
  noindex: false,
  children: null,
}

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  path: PropTypes.string,
  image: PropTypes.string,
  type: PropTypes.string,
  noindex: PropTypes.bool,
  children: PropTypes.node,
}

export default Seo
